/* eslint-disable unicorn/consistent-destructuring */

/**
 * @typedef {import('tinymce').Editor} Editor
 */

import $ from 'jquery';
import { isPlainObject, isPromise } from './types.js';
import MpHtmlEditor from '../mpro/dynamic-plugins/widgets/html-editor/Widget.js';

/**
 * @typedef {{ url: string; attrName?: string; dataName?: string; dataIsObject?: boolean; }} ElementOriginalUrlObject
 */

/**
 * @param {any} value
 * @param {JQuery} context
 */
export const decodeKeyword = (value, context) => {

	if (typeof value !== 'string') {
		return value;
	}

	switch (value) {
		case ':self': {
			return context;
		}
		case ':parent': {
			return context.parent();
		}
		case ':prev': {
			return context.prev();
		}
		case ':next': {
			return context.next();
		}
		case ':first': {
			return context.first();
		}
		case ':last': {
			return context.last();
		}
		case ':children': {
			return context.children();
		}
		case ':siblings': {
			return context.siblings();
		}
		case ':value':
		case '##value##': {
			return context.val();
		}
		case ':text': {
			return context.text();
		}
		// No default
	}

	return value;
};

/**
 * @param {{ [key: string]: any; }} obj
 * @param {JQuery} context
 */
export const decodeObjectKeywords = (obj, context) => {

	for (const [key, value] of Object.entries(obj)) {
		if (typeof value === 'string') {
			obj[key] = decodeKeyword(value, context);
		}
		else if (isPlainObject(value) || Array.isArray(value)) {
			obj[key] = decodeObjectKeywords(value, context);
		}
	}

	return obj;
};

/**
 * Get the first url value from the element (look inside the dataset also)
 *
 * @param {HTMLElement|JQuery} element
 * @param {boolean} saveData
 *
 * @return {ElementOriginalUrlObject}
 */
export const getElementUrl = (element, saveData = true) => {

	/** @type {JQuery<HTMLElement>} */// @ts-ignore
	const $elem = element.jquery ? element : $(element);
	const data = $elem.data() || {};
	const firstTime = !('_originalUrl' in data);
	const { _originalUrl = {} } = data;

	// First time access
	if (firstTime) {

		// Handle data-modal-url
		if (data.modalUrl) {
			_originalUrl.url = data.modalUrl;
			_originalUrl.dataName = 'modalUrl';
		}

		// Handle data-modal options
		else if (data.modal && data.modal.url) {
			_originalUrl.url = data.modal.url;
			_originalUrl.dataName = 'modal';
			_originalUrl.dataIsObject = true;
		}

		// Handle data-drawer-url
		else if (data.drawerUrl) {
			_originalUrl.url = data.drawerUrl;
			_originalUrl.dataName = 'drawerUrl';
		}

		// Handle data-drawer options
		else if (data.drawer && data.drawer.url) {
			_originalUrl.url = data.drawer.url;
			_originalUrl.dataName = 'drawer';
			_originalUrl.dataIsObject = true;
		}

		// Handle data-mp-remote-action options
		else if (data.mpRemoteAction && data.mpRemoteAction.url) {
			_originalUrl.url = data.mpRemoteAction.url;
			_originalUrl.dataName = 'mpRemoteAction';
			_originalUrl.dataIsObject = true;
		}

		// Handle data-mp-remote-action-url
		else if (data.mpRemoteActionUrl) {
			_originalUrl.url = data.mpRemoteActionUrl;
			_originalUrl.dataName = 'mpRemoteActionUrl';
		}

		// Handle data-mp-dt-remote-action options
		else if (data.mpDtRemoteAction && data.mpDtRemoteAction.url) {
			_originalUrl.url = data.mpDtRemoteAction.url;
			_originalUrl.dataName = 'mpDtRemoteAction';
			_originalUrl.dataIsObject = true;
		}

		// Handle data-mp-dt-remote-action-url
		else if (data.mpDtRemoteActionUrl) {
			_originalUrl.url = data.mpDtRemoteActionUrl;
			_originalUrl.dataName = 'mpDtRemoteActionUrl';
		}

		// Handle data-url
		else if (data.url) {
			_originalUrl.url = data.url;
			_originalUrl.dataName = 'url';
		}

		// Handle data-href
		else if (data.href) {
			_originalUrl.url = data.href;
			_originalUrl.dataName = 'href';
		}

		// Handle native href attribute
		else {
			const isForm = element instanceof HTMLFormElement;
			_originalUrl.url = isForm ? $elem.attr('action') : $elem.attr('href');
			_originalUrl.attrName = isForm ? 'action' : 'href';
		}

		data._originalUrl = _originalUrl;

		// Make sure data si saved
		if (saveData === true) {
			$elem.data(data);
		}
	}

	return _originalUrl;
};

/**
 * Set the element url to where it first came from
 *
 * @param {HTMLElement|JQuery} element
 * @param {string} url
 * @param {ElementOriginalUrlObject=} cache
 */
export const setElementUrl = (element, url, cache) => {

	/** @type {JQuery<HTMLElement>} */// @ts-ignore
	const $elem = element.jquery ? element : $(element);
	const elem = $elem[0];
	const data = $elem.data();

	if (!cache) {
		cache = getElementUrl(elem);
	}

	// Set new href in data & attribute
	if (cache.dataName) {
		// Stringify the attribute
		if (cache.dataIsObject) {
			data[cache.dataName] = { ...data[cache.dataName], url };
			elem.dataset[cache.dataName] = JSON.stringify(data[cache.dataName]);
		}

		// Just set the value
		else {
			data[cache.dataName] = url;
			elem.dataset[cache.dataName] = url;
		}
	}
	// Set new href in attribute
	else {
		$elem.attr(element instanceof HTMLFormElement ? 'action' : 'href', url);
	}
};

/**
 * @param { HTMLSelectElement | JQuery<HTMLSelectElement> } select
 * @param {any} value
 * @param {boolean=} append
 */
export const setSelectValue = (select, value, append = false, triggerChange = true) => {

	/** @type {JQuery<HTMLSelectElement>} */ // @ts-ignore
	const $select = select.jquery ? select : $(select);
	const arrayValue = Array.isArray(value) ? value : (value != null ? [value] : []);

	// Clear results first
	if (!append) {
		$select.val(null);
	}

	// Check if select values exist
	for (const value of arrayValue) {

		let id;
		let text;

		if (value != null && typeof value === 'object') {
			// eslint-disable-next-line prefer-destructuring
			id = value.id;
			// eslint-disable-next-line prefer-destructuring
			text = value.text;
		}
		else {
			id = value;
		}

		if (!text) {
			text = `${mpro.langvar('undefined_value')} (${id})`;
		}

		// Check if the option exists
		if ($select.find(`option[value="${id}"]`).length === 0) {
			$select.append(new Option(text, id, true, true));
		}
		else {
			$select.find(`option[value="${id}"]`).prop('selected', true);
		}
	}

	if (triggerChange) {
		$select.triggerNative('change');
	}

	return $select;
};

/**
 * @param {HTMLInputElement | HTMLTextAreaElement} el
 * @param {string} text
 */
export const insertTextAtCursor = (el, text, selectAfterInsert = true) => {

	const val = el.value || '';
	const startIndex = el.selectionStart;
	const endIndex = el.selectionEnd;

	el.focus();
	el.value = val.slice(0, startIndex) + text + val.slice(endIndex);
	el.selectionStart = selectAfterInsert ? startIndex : (startIndex + text.length);
	el.selectionEnd = startIndex + text.length;
};

/**
 * @param {JQuery | HTMLElement} _field
 * @param {any} value
 * @param {boolean} append
 */
export const setFieldValue = (_field, value, append = false, triggerChange = true) => {

	if (!_field) {
		return;
	}

	/** @type {JQuery<HTMLElement>} */ // @ts-ignore
	const $field = _field.jquery ? _field : $(_field);
	const field = $field[0];
	let editor;

	// Handle select
	if (field.tagName === 'SELECT') {

		const select2 = $field.data('select2');

		// Use custom select2.setSelection() method if available
		// @ts-ignore
		if (select2 && typeof select2.setSelection === 'function') {
			// @ts-ignore
			select2.setSelection(value, append, triggerChange);
		}
		else {
			// @ts-ignore
			setSelectValue($field, value, append, triggerChange);
		}
	}

	// Handle checkbox/radio
	// @ts-ignore
	else if (field.type === 'checkbox' || field.type === 'radio') {
		$field.prop('checked', Boolean(value));
		if (triggerChange) {
			$field.triggerNative('change');
		}
	}

	// Handle html editor (textarea or editable div?)
	else if ((editor = MpHtmlEditor.getInstanceRef($field))) {

		/**
		 * @param {boolean} append
		 * @param {Editor} editor
		 * @param {string} content
		 */
		const setContent = (function(append, value, editor) {
			if (append) {
				editor.insertContent(value || '');
			}
			else {
				editor.setContent(value || '');
			}
		}).bind(null, append, value);

		if (MpHtmlEditor.isInstanceLoaded(editor)) {
			setContent(editor);
		}
		else if (isPromise(editor)) {
			// @ts-ignore
			editor.then((/** @type {Editor} */ editor) => {
				setContent(editor);
			});
		}
		else {
			console.warn('Failed to set html editor content (element: %o)', $field);
		}
	}

	// Handle input/textarea
	else {
		if (append) {
			// @ts-ignore
			insertTextAtCursor($field[0], value);
		}
		else {
			$field.val(value);
		}

		if (triggerChange) {
			$field.triggerNative('change');
		}
	}
};